import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Modal,
  Typography,
  Divider,
  Alert,
  Space,
  Popover,
} from "antd";
import {
  RedoOutlined,
  SyncOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import { GET, POST } from "../../utils/HttpClient";
import { Toast } from "../../utils/Toast";
import { URL_SYNC_DATA } from "../../constance/urls";
import { ellipsisString, formatComma } from "../../utils/stringFormat";
import * as _ from "lodash";
import ColorButton from "../common/ColorButton";
import { BLUE_COLOR } from "../../constance/color";
import TSTable from "../common/TSTable";

export default function UpdateDataModal(props) {
  const { open, onClose, syncUrl, syncType, activeMenu } = props;

  const ref = React.useRef();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessages, setErrorMessages] = React.useState(null);
  const [isHandleEnabled, setIsHandleEnabled] = React.useState(false);

  const columns = React.useMemo(() => [
    {
      title: t("date"),
      dataIndex: "date",
      render: (_, original) =>
        new Date(original.created).toLocaleString("en-GB"),
    },
    {
      title: t("sync_data_transactions.sync_count"),
      dataIndex: "sync_count",
      render: (_, original) => (
        <div style={{ textAlign: "center" }}>
          {formatComma(original.sync_count)}
        </div>
      ),
    },
    {
      title: t("sync_data_transactions.is_sync_success"),
      dataIndex: "is_sync_success",
      render: (_, original) =>
        original.is_sync_success == null ? (
          t("progressing")
        ) : (
          <div style={{ textAlign: "center" }}>
            {original.is_sync_success ? (
              <CheckOutlined style={{ color: "green" }} />
            ) : (
              <CloseOutlined style={{ color: "red" }} />
            )}
          </div>
        ),
    },
    {
      title: t("sync_data_transactions.is_update_success"),
      dataIndex: "is_update_success",
      render: (_, original) =>
        (original.is_sync_success && original.is_update_success == null) ||
        original.is_update_success == null ? (
          t("progressing")
        ) : (
          <div style={{ textAlign: "center" }}>
            {original.is_update_success ? (
              <CheckOutlined style={{ color: "green" }} />
            ) : (
              <CloseOutlined style={{ color: "red" }} />
            )}
          </div>
        ),
    },
    {
      title: t("detail"),
      dataIndex: "error_message",
      render: (_, original) =>
        original.error_message ? (
          <div>
            <Popover content={ellipsisString(original.error_message, 300)}>
              {" "}
              <span>{ellipsisString(original.error_message, 30)}</span>
            </Popover>
          </div>
        ) : (
          "-"
        ),
    },
  ]);

  const handleProcess = async () => {
    setIsLoading(true);
    setErrorMessages(null);
    try {
      await POST(syncUrl);
      Toast.success(t("progressing"));
      setIsHandleEnabled(false);
      if (ref.current) {
        ref.current.refetch();
      }
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const checkSyncData = async () => {
    setIsLoading(true);
    setErrorMessages(null);
    try {
      const response = await GET(URL_SYNC_DATA.CHECK_SYNC_DATA, {
        sync_type: syncType,
      });
      setIsHandleEnabled(!_.get(response, "data.is_working", false));
    } catch (error) {
      setErrorMessages(error.errorMessages);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshStatus = () => {
    checkSyncData();
    if (ref.current) {
      ref.current.fetch();
    }
  };

  React.useEffect(() => {
    if (open) {
      setErrorMessages(null);
      checkSyncData();
    }
  }, [open]);

  return (
    <div>
      <Modal
        centered
        open={open}
        title={
          <Typography.Title level={3}>
            {activeMenu ? t(`admins.${activeMenu}.title`) : ""}
            <Divider />
          </Typography.Title>
        }
        width={800}
        onCancel={() => {
          if (!isLoading) {
            onClose();
          }
        }}
        footer={null}
      >
        {errorMessages && (
          <Alert style={{ maxHeight: 300, overflow: "auto" }} error>
            {errorMessages}
          </Alert>
        )}

        {isHandleEnabled ? (
          <ColorButton
            type="primary"
            override={BLUE_COLOR}
            loading={isLoading}
            onClick={handleProcess}
            icon={<SyncOutlined />}
          >
            {t("sync_data_transactions.confirm")}
          </ColorButton>
        ) : (
          <ColorButton override={BLUE_COLOR} disabled>
            {t("progressing")}
          </ColorButton>
        )}

        <Divider />
        <div style={{ display: "flex", alignItems: "baseline" }}>
          <Space>
            <Typography.Title level={4} floated="left">
              {t("sync_data_tranaction")}
            </Typography.Title>
            <Button basic icon={<RedoOutlined />} onClick={refreshStatus} />
          </Space>
        </div>
        <br />
        <span className="exportItemList-table">
          <TSTable
            ref={ref}
            url={URL_SYNC_DATA.SYN_DATA_TRANSACTION}
            params={{ sync_type: syncType }}
            columns={columns}
            scroll={{ x: 1000 }}
          />
        </span>
      </Modal>
    </div>
  );
}
